import { Combobox as ComboboxPrimitive } from "@base-ui/react/combobox";
import { Check, ChevronDown } from "lucide-react";
import { fieldControlStyles } from "@/components/ui/control-styles";
import { cn } from "@/lib/utils";

/** shadcn Combobox composition, adapted to this console's tokens and Base UI stack. */
export function Combobox({
  options,
  value,
  onValueChange,
  placeholder,
  emptyLabel = "Use the exact action value you typed.",
  triggerLabel = "Show action suggestions",
  className,
  id,
  disabled,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
}: {
  options: readonly string[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  emptyLabel?: string;
  triggerLabel?: string;
  className?: string;
  id?: string;
  disabled?: boolean;
  "aria-label"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}) {
  return (
    <ComboboxPrimitive.Root
      items={options}
      value={value || null}
      inputValue={value}
      onInputValueChange={(nextValue) => onValueChange(nextValue)}
      onValueChange={(nextValue) => {
        if (nextValue !== null) onValueChange(nextValue);
      }}
      autoHighlight
      disabled={disabled}
    >
      <div className="relative flex h-9 w-full items-center pointer-coarse:h-11">
        <ComboboxPrimitive.Input
          id={id}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          placeholder={placeholder}
          className={cn(
            "flex h-9 w-full rounded-sm px-3.5 pr-9 text-sm text-ink placeholder:text-muted pointer-coarse:h-11 pointer-coarse:pr-11",
            fieldControlStyles,
            className,
          )}
        />
        <ComboboxPrimitive.Trigger
          aria-label={triggerLabel}
          className="absolute inset-y-0 right-0 inline-flex h-9 w-9 items-center justify-center rounded-r-sm text-muted hover:text-ink focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus disabled:pointer-events-none pointer-coarse:h-11 pointer-coarse:w-11"
        >
          <ChevronDown className="size-3.5" aria-hidden="true" />
        </ComboboxPrimitive.Trigger>
      </div>
      <ComboboxPrimitive.Portal>
        <ComboboxPrimitive.Positioner
          align="start"
          sideOffset={6}
          className="z-50"
        >
          <ComboboxPrimitive.Popup className="max-h-[min(18rem,var(--available-height))] min-w-(--anchor-width) overflow-hidden rounded-lg border border-line bg-panel shadow-panel outline-hidden transition-all duration-150 data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0 motion-reduce:transform-none motion-reduce:transition-none">
            <ComboboxPrimitive.Empty className="px-3 py-2 text-sm text-muted">
              {emptyLabel}
            </ComboboxPrimitive.Empty>
            <ComboboxPrimitive.List className="max-h-[min(18rem,var(--available-height))] overflow-y-auto p-1 console-scrollbar">
              {(option: string) => (
                <ComboboxPrimitive.Item
                  key={option}
                  value={option}
                  className="grid min-h-9 cursor-default grid-cols-[1rem_minmax(0,1fr)] items-center gap-2 rounded-sm px-2 py-2 text-sm text-ink data-highlighted:bg-panel-raised data-highlighted:font-medium data-highlighted:outline-solid data-highlighted:outline-2 data-highlighted:outline-focus data-highlighted:-outline-offset-2 forced-colors:data-highlighted:outline-[Highlight] pointer-coarse:min-h-11"
                >
                  <ComboboxPrimitive.ItemIndicator>
                    <Check className="size-3.5" aria-hidden="true" />
                  </ComboboxPrimitive.ItemIndicator>
                  <span className="col-start-2 min-w-0 truncate">{option}</span>
                </ComboboxPrimitive.Item>
              )}
            </ComboboxPrimitive.List>
          </ComboboxPrimitive.Popup>
        </ComboboxPrimitive.Positioner>
      </ComboboxPrimitive.Portal>
    </ComboboxPrimitive.Root>
  );
}
